import { Directive, Injector, Optional } from '@angular/core';
import { DynamicComponentRefProvider, runtimeEventManager } from "@wm/core";
import {
  IWidgetConfig,
  provideAsWidgetRef,
  StylableComponent,
  styler,
  APPLY_STYLES_TYPE,
  register,
  Parent
} from '@wm/components-base';

const DEFAULT_CLS = 'app-linear-layout clearfix';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-linearlayout',
  hostClass: DEFAULT_CLS
};
const hAlignValues: any = {
  'left': 'flex-start',
  'right': 'flex-end',
  'center': 'center',
},
  vAlignValues: any = {
    'top': 'flex-start',
    'bottom': 'flex-end',
    'center': 'center',
  };
@Directive({
  selector: '[wmLinearLayout]',
  standalone: true,
  exportAs: 'wmLinearLayout',
  providers: [
    provideAsWidgetRef(LinearLayoutDirective)
  ]
})
export class LinearLayoutDirective extends StylableComponent {
  static initializeProps = register('wm-linearlayout');
  private dynamicComponentProvider: DynamicComponentRefProvider;
  public direction: any;

  constructor(inj: Injector, @Optional() viewParent: Parent) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this, APPLY_STYLES_TYPE.CONTAINER);
    this.dynamicComponentProvider = runtimeEventManager.getService('dynamicComponentRefProvider');
  }

  protected override onPropertyChange(key: string, nv: any, ov?: any) {
    switch (key) {
      case 'direction':
        this.changeDirection(this.$element, nv);
        break;
      case 'horizontalalign':
        this.$element.css('justify-content', hAlignValues[nv]);
        break;
      case 'verticalalign':
        this.$element.css('justify-content', vAlignValues[nv]);
        break;
      case 'spacing':
        this.removeClass(this.$element, 'app-linear-layout-spacing');
        this.$element.addClass('app-linear-layout-spacing-' + nv);
        break;
    }
    super.onPropertyChange(key, nv, ov);
  }

  protected override onStyleChange(key: string, nv: any, ov?: any) {
    if (key === 'horizontalalign') {
      if (this.direction === 'row' || this.direction === 'row-reverse') {
        this.$element.css('justify-content', hAlignValues[nv]);
      } else {
        this.$element.css('align-items', hAlignValues[nv]);
      }
    } else if (key === 'verticalalign') {
      if (this.direction === 'column' || this.direction === 'column-reverse') {
        this.$element.css('justify-content', vAlignValues[nv]);
      } else {
        this.$element.css('align-items', vAlignValues[nv]);
      }
    }
    super.onStyleChange(key, nv, ov);
  }

  private removeClass($el: any, prefix: any) {
    $el.removeClass((i: any, className: any) => {
      return className.split(' ')
        .filter((s: string) => s.startsWith(prefix))
        .join(' ');
    });
  }

  private changeDirection($el: any, v: any) {
    this.removeClass($el, 'app-linear-layout-direction');
    $el.addClass('app-linear-layout-direction-' + v);
    if (v === 'row' || v === 'row-reverse') {
      $el.css('justify-content', hAlignValues[this.horizontalalign]);
    } else if (v === 'column' || v === 'column-reverse') {
      $el.css('justify-content', vAlignValues[this.verticalalign]);
    }
    $el.css('flex-direction', v);
  }

  async buildActionPanel(el:any, actionLocation:any) {
    let pScope: any = {};
    pScope.actions = this.getDesignActions(this, el, actionLocation);
    pScope.showPanel = () => {
      return (actionLocation === 'right'
        && (this.direction === 'row'
          || this.direction === 'row-reverse'))
        || (actionLocation === 'bottom'
          && (this.direction === 'column'
            || this.direction === 'column-reverse')
          || ((actionLocation === 'right'
            || actionLocation === 'bottom')
            && el.find('>.app-linear-layout-item').length <= 1));
    };

    let template = '<wms-design-quick-actions [actions]="actions" [show]="showPanel"/>';
    let element = await this.dynamicComponentProvider.addComponentBasedOnSelector(null, template, pScope, {}).then((el: HTMLElement) => {
      return el;
    });
    return element;
  }

  private getDesignActions($scope:any, $el: any, actionLocation:any) {
    var widgetId = $el.attr('widgetid');
    return [{
      'name': 'add',
      'icon': 'wms wms-plus',
      'onClick': function (event: any) {
        event.stopPropagation();
        var lastItem = $el.find('>.app-linear-layout-item:last')[0].widget,
          widgetDetails = {
            parentId: widgetId,
            widgetType: 'wm-linearlayoutitem',
            widgetDefaults: {
              flexgrow: (lastItem && lastItem.flexgrow !== undefined) ? lastItem.flexgrow : 1
            },
            preventWidgetSelection: true
          };
        runtimeEventManager.emit('canvas-add-widget', widgetDetails);
        var direction = $scope.direction;
        if (actionLocation === 'right'
          && !($scope.direction === 'row'
            || $scope.direction === 'row-reverse')) {
          direction = 'row';
        }
        if (actionLocation === 'bottom'
          && !($scope.direction === 'column'
            || $scope.direction === 'column-reverse')) {
          direction = 'column';
        }
        if (direction !== $scope.direction) {
          runtimeEventManager.emit('update-widget-property',{prop: 'direction', value: direction});
          runtimeEventManager.emit('set-markup-attr', {widgetId, 'direction': direction});
        }
      }
    }, {
      'name': 'remove',
      'icon': 'wms wms-remove',
      'show': function () {
        return $el.find('>.app-linear-layout-item').length > 1;
      },
      'onClick': function (event: any) {
        event.stopPropagation();
        var itemToRemove = $el.find('>.app-linear-layout-item:last').attr('widgetid') || widgetId;
        var widgetDetails = { widgetId: itemToRemove };
        runtimeEventManager.emit('canvas-delete-widget', widgetDetails);
      }
    }];
  }
}
